import React, { useEffect, Suspense } from 'react';
devLog.log("EP-BINARY-LOCK-V4.1");
import '@/App.css';
import '@/styles/contentEnhancement.css';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { Header } from './components/layout/Header';
import { Footer } from './components/layout/Footer';
import { ResultRouter } from './components/ResultRouter';
import { PublicResult } from './components/PublicResult';
import { ScrollToTop } from './components/ScrollToTop';
import { SessionHealthBanner } from './components/SessionHealthBanner';
import ErrorBoundary from './components/ErrorBoundary';
import { syncWithDeployment } from './utils/deploymentSyncManager';
import { Home } from './components/Home';

// V20.1: Cache management for production deployments
import { 
  initCacheManagement, 
  BUILD_VERSION,
  autoCleanCacheOnLoad,
  clearCreditCache
} from './utils/cacheManager';

// V25.3.0: Silent update manager for seamless updates
import silentUpdateManager from './utils/silentUpdateManager';

// V37.42.0: Cold-start warm-up — primes backend lazy modules on app load
// + window focus + visibility change.
import useColdStartWarmup from './utils/useColdStartWarmup';

import devLog from './utils/devLog';

// ═══════════════════════════════════════════════════════════════════════════════
// V24.99.7: LAZY LOADING WITH RETRY - Safari/Cross-browser compatibility fix
// Implements retry logic for chunk loading failures after deployments
// ═══════════════════════════════════════════════════════════════════════════════
const lazyWithRetry = (componentImport, retries = 3, interval = 1000) => {
  return React.lazy(async () => {
    for (let i = 0; i < retries; i++) {
      try {
        return await componentImport();
      } catch (error) {
        devLog.warn(`[LazyLoad V24.99.7] Attempt ${i + 1} failed:`, error.message);
        
        // If it's a ChunkLoadError and we have retries left, wait and try again
        if (i < retries - 1 && (
          error.name === 'ChunkLoadError' || 
          error.message?.includes('Loading chunk') ||
          error.message?.includes('ChunkLoadError')
        )) {
          await new Promise(resolve => setTimeout(resolve, interval));
          
          // On final retry, try clearing caches first
          if (i === retries - 2 && 'caches' in window) {
            devLog.log('[LazyLoad V24.99.7] Clearing caches before final retry');
            const names = await caches.keys();
            await Promise.all(names.map(name => caches.delete(name)));
          }
          continue;
        }
        throw error;
      }
    }
  });
};

// Lazy load non-critical routes with retry logic for better Safari compatibility
// V37.44.31 — Credits page removed from user-facing routing (backend ledger untouched).
const Account = lazyWithRetry(() => import('./components/Account').then(m => ({ default: m.Account })));
const AuthCallback = lazyWithRetry(() => import('./components/AuthCallback'));
const Settings = lazyWithRetry(() => import('./components/Settings').then(m => ({ default: m.Settings })));
const Pricing = lazyWithRetry(() => import('./components/Pricing').then(m => ({ default: m.Pricing })));
const Contact = lazyWithRetry(() => import('./components/Contact').then(m => ({ default: m.Contact })));
const Tutorial = lazyWithRetry(() => import('./components/Tutorial').then(m => ({ default: m.Tutorial })));
const About = lazyWithRetry(() => import('./components/About').then(m => ({ default: m.About })));
const Privacy = lazyWithRetry(() => import('./components/Privacy').then(m => ({ default: m.Privacy })));
const Terms = lazyWithRetry(() => import('./components/Terms').then(m => ({ default: m.Terms })));
const PaymentSuccess = lazyWithRetry(() => import('./components/PaymentSuccess').then(m => ({ default: m.PaymentSuccess })));

// V37.32.0: Prompt category list pages (Project Prompts / Bug Fix Prompts)
const PromptList = lazyWithRetry(() => import('./components/prompts/PromptList').then(m => ({ default: m.PromptList })));

// Admin Dashboard - Isolated Admin Layer (ADDITIVE)
const AdminDashboard = lazyWithRetry(() => import('./admin/AdminDashboard'));

// ═══════════════════════════════════════════════════════════════════════════════
// V25.3.0: NAVIGATION TRACKER - Triggers silent update activation on safe navigation
// ═══════════════════════════════════════════════════════════════════════════════
const NavigationTracker = () => {
  const location = useLocation();
  
  useEffect(() => {
    // Track navigation for silent update system
    silentUpdateManager.onNavigate(location.pathname);
  }, [location.pathname]);
  
  return null; // This component renders nothing
};

export default function App() {
  // V37.42.0: trigger non-blocking backend warmup (mount + focus + visibility).
  useColdStartWarmup();

  // V25.0.2: Initialize cache management, desktop zoom, and auto-clean on app startup
  useEffect(() => {
    devLog.log(`[APP] ElonPrompt AI v${BUILD_VERSION} starting...`);
    
    // V32.4.0: Deployment-aware cache sync — detects backend deployments
    // and surgically clears stale credit/result data without logging users out
    syncWithDeployment().then(purged => {
      if (purged) devLog.log('[APP V32.4.0] Deployment sync: stale data purged');
    }).catch(() => {});
    
    // V32.1.0: Automatic cache/cookie clearing on every app load
    autoCleanCacheOnLoad();
    clearCreditCache();
    
    // Initialize cache management
    initCacheManagement().catch(err => {
      devLog.warn('[APP] Cache management init failed:', err);
    });
  }, []);

  return (
    <ErrorBoundary>
      <AuthProvider>
        <div className="App">
          <BrowserRouter>
            <NavigationTracker />
            <ScrollToTop />
            <SessionHealthBanner />
            <Suspense fallback={
              <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
                  <p className="mt-4 text-gray-600">Loading...</p>
                </div>
              </div>
            }>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/result" element={<ResultRouter Header={Header} Footer={Footer} />} />
                <Route path="/results/:sessionId" element={<PublicResult Header={Header} Footer={Footer} />} />
                {/* V37.44.31 — /credits route removed for free-access mode; user-facing Credit Balance UI hidden. */}
                <Route path="/account" element={<Account Header={Header} Footer={Footer} />} />
                <Route path="/settings" element={<Settings Header={Header} Footer={Footer} />} />
                {/* V37.44.9 — /pricing route removed for free-access mode; catch-all below redirects to home. */}
                <Route path="/contact" element={<Contact Header={Header} Footer={Footer} />} />
                <Route path="/tutorial" element={<Tutorial Header={Header} Footer={Footer} />} />
                <Route path="/about" element={<About Header={Header} Footer={Footer} />} />
                <Route path="/privacy" element={<Privacy Header={Header} Footer={Footer} />} />
                <Route path="/terms" element={<Terms Header={Header} Footer={Footer} />} />
                <Route path="/payment-success" element={<PaymentSuccess Header={Header} Footer={Footer} />} />

                {/* V37.32.0: Prompt category lists */}
                <Route path="/prompts/projects" element={<PromptList category="projects" Header={Header} Footer={Footer} />} />
                <Route path="/prompts/bug-fixes" element={<PromptList category="bug-fixes" Header={Header} Footer={Footer} />} />
                {/* Google OAuth callback route - handles redirect from Google */}
                <Route path="/auth/google" element={<AuthCallback />} />
                {/* Admin Dashboard - Isolated Admin Layer (ADDITIVE) */}
                <Route path="/admin" element={<AdminDashboard />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </div>
      </AuthProvider>
    </ErrorBoundary>
  );
}
